import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { HTTPResponse, APP_CONFIG } from '@mavles/shared';
import { IUser, IRole } from '@models/user/user';

export interface CreateUserPayload {
  firstname: string;
  lastname: string;
  email: string;
  password: string;
  role_ids: number[];
  image?: string;
}

export interface UpdateUserPayload {
  firstname: string;
  lastname: string;
  email: string;
  password?: string;
  role_ids: number[];
  image?: string;
}

export interface ResetPasswordPayload {
  new_password: string;
  new_password_confirmation: string;
}

export interface ChangePasswordPayload {
  currentPassword: string;
  newPassword: string;
}

@Injectable({ providedIn: 'root' })
export class UserApiService {
  private http = inject(HttpClient);
  private config = inject(APP_CONFIG);

  private baseURL = `${this.config.apiUrl}user`;

  // ─── Existing (profile) ───────────────────────────────────────────────────

  getAllUsers(): Observable<HTTPResponse<IUser[]>> {
    return this.http.get<HTTPResponse<IUser[]>>(`${this.baseURL}/getAllUsers`);
  }

  getUserByID(id: number): Observable<HTTPResponse<IUser>> {
    return this.http.get<HTTPResponse<IUser>>(`${this.baseURL}/get/${id}`);
  }

  updateUser(user: IUser): Observable<HTTPResponse<IUser>> {
    return this.http.post<HTTPResponse<IUser>>(`${this.baseURL}/updateUser`, user);
  }

  updateUserPassword(user: IUser): Observable<HTTPResponse<IUser>> {
    return this.http.post<HTTPResponse<IUser>>(`${this.baseURL}/updateUserPassword`, user);
  }

  deleteUser(user: IUser): Observable<any> {
    return this.http.post(`${this.baseURL}/deleteUser`, user);
  }

  // ─── Admin: User Management ───────────────────────────────────────────────

  /** GET /getAllUsers - list all with roles & is_active */
  listAllUsers(): Observable<HTTPResponse<IUser[]>> {
    return this.http.get<HTTPResponse<IUser[]>>(`${this.baseURL}/getAllUsers`);
  }

  /** GET /get/{id} */
  getUser(id: number): Observable<HTTPResponse<IUser>> {
    return this.http.get<HTTPResponse<IUser>>(`${this.baseURL}/get/${id}`);
  }

  /** POST /store */
  createUser(payload: CreateUserPayload): Observable<HTTPResponse<IUser>> {
    return this.http.post<HTTPResponse<IUser>>(`${this.baseURL}/store`, payload);
  }

  /** PUT /update/{id} */
  updateUserAdmin(id: number, payload: UpdateUserPayload): Observable<HTTPResponse<IUser>> {
    return this.http.put<HTTPResponse<IUser>>(`${this.baseURL}/update/${id}`, payload);
  }

  /** POST /reset-password/{id} — admin resets any user password */
  adminResetPassword(id: number, payload: ResetPasswordPayload): Observable<HTTPResponse<any>> {
    return this.http.post<HTTPResponse<any>>(`${this.baseURL}/reset-password/${id}`, payload);
  }

  /** PATCH /toggle-status/{id} */
  toggleStatus(id: number, is_active: boolean): Observable<HTTPResponse<any>> {
    return this.http.patch<HTTPResponse<any>>(`${this.baseURL}/toggle-status/${id}`, { is_active });
  }

  /** POST /change-password — self-service */
  changePassword(payload: ChangePasswordPayload): Observable<HTTPResponse<any>> {
    return this.http.post<HTTPResponse<any>>(`${this.baseURL}/change-password`, payload);
  }

  /** POST /avatar — upload profile photo (File or base64) */
  uploadAvatar(fileOrBase64: File | string): Observable<HTTPResponse<any>> {
    if (fileOrBase64 instanceof File) {
      const formData = new FormData();
      formData.append('avatar', fileOrBase64);
      return this.http.post<HTTPResponse<any>>(`${this.baseURL}/avatar`, formData);
    } else {
      return this.http.post<HTTPResponse<any>>(`${this.baseURL}/avatar`, { image: fileOrBase64 });
    }
  }

  /** DELETE /avatar — remove profile photo */
  deleteAvatar(): Observable<HTTPResponse<any>> {
    return this.http.delete<HTTPResponse<any>>(`${this.baseURL}/avatar`);
  }

  // ─── Roles ───────────────────────────────────────────────────────────────

  getRoles(): Observable<HTTPResponse<IRole[]>> {
    return this.http.get<HTTPResponse<IRole[]>>(`${this.config.apiUrl}admin/user/roles`);
  }
}
